import { BaseSymbol } from '@ui/assets/config/trade-pair-listings';
import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Edit, XCircle } from '@ui/components/icons';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { useTokenSizeDisplayDecimals } from '@ui/hooks/useTokenSizeDisplayDecimals';
import { toReadableValue } from '@ui/lib/bignumber';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import type { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3/types';
import BigNumber from 'bignumber.js';
import { useMemo, useState } from 'react';
import { Grid, HStack, Stack, styled } from 'styled-system/jsx';

import { useInvalidatePositionQuery } from '../../hooks/useInvalidateQuery';
import { useTradingPair } from '../../store/useTradingPair';
import { EditDecreaseOrderOverlay } from '../DecreaseOrder/EditDecreaseOrderOverlay';
import { TradeLeverageBadge } from '../LeverageBadge';
import { CurrentTradingPairToggle } from '../OrderList/CurrentTradingPairToggle';
import { getSidePillProps } from '../PositionList/styles';
import { PairSideCell, PositionSidePill, SidePill } from '../shared/PairSideCell';

const getFilterIsCurrentPair =
  (baseSymbol: BaseSymbol, positionsByHash: Record<string, Position>) =>
  (item: DecreaseOrderRow) => {
    return positionsByHash[item.positionHash]?.pair.baseSymbol === baseSymbol;
  };

// fixed-content columns (Type pill, Action icons) get FIXED tracks — letting
// them stretch left hollow voids beside 42px pills; data columns share the
// rest in even, content-proportional steps
const TABLE_COLUMNS =
  'minmax(170px, 1.2fr) 64px minmax(140px, 1fr) minmax(150px, 1fr) minmax(110px, 0.8fr) 88px';

export const DecreaseOrderList = ({
  orders,
  positions,
  emptyView,
  showCurrentPairToggle = true,
}: {
  orders: DecreaseOrderRow[];
  positions: Position[];
  emptyView: React.ReactNode;
  showCurrentPairToggle?: boolean;
}) => {
  const [isCurrentPairOnly, setIsCurrentPairOnly] = useState(false);
  const tradingPair = useTradingPair((v) => v.useTradingPair());
  const positionsByHash = useMemo(
    () =>
      positions.reduce<Record<string, Position>>((acc, position) => {
        acc[position.id] = position;

        return acc;
      }, {}),
    [positions],
  );
  const filteredOrders = useMemo(() => {
    const filters: ((item: DecreaseOrderRow) => boolean)[] = [];
    if (isCurrentPairOnly) {
      filters.push(
        getFilterIsCurrentPair(tradingPair.baseSymbol, positionsByHash),
      );
    }

    return filters.reduce((acc, filter) => acc.filter(filter), orders);
  }, [isCurrentPairOnly, orders, positionsByHash, tradingPair.baseSymbol]);

  return (
    <>
      {showCurrentPairToggle && (
        <CurrentTradingPairToggle
          css={{
            justifyContent: 'end',
            height: '0',
            // float into the 40px V2 tab bar, vertically centered
            transform: 'translateY(-20px) translateX(-10px)',
          }}
          display={{ base: 'none', desktop: 'flex' }}
          checked={isCurrentPairOnly}
          onCheckedChange={(v) => {
            setIsCurrentPairOnly(v.checked);
          }}
        />
      )}
      <Grid
        gridTemplateColumns={TABLE_COLUMNS}
        columnGap={3}
        rowGap={0}
        px={4}
        display={{ base: 'none', desktop: 'grid' }}
        h="32px"
        alignItems="center"
        borderBottom="1px solid"
        bg="v2.bg.app"
        borderBottomColor="v2.border.hairline"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Header>Pair & Side</Header>
        <Header>Type</Header>
        <Header>Trigger / Mark</Header>
        <Header>Close Qty</Header>
        <Header>Est. PnL</Header>
        <Header>Action</Header>
      </Grid>
      <Stack gap="0">
        {filteredOrders.map((order) => (
          <DecreaseOrderCard
            key={order.orderHash}
            order={order}
            position={positionsByHash[order.positionHash]}
          />
        ))}
      </Stack>
      {filteredOrders.length === 0 && emptyView}
    </>
  );
};

const Header = ({
  children,
  align = 'start',
}: {
  children: React.ReactNode;
  align?: 'start' | 'end';
}) => (
  <Stack gap={1} alignItems={align === 'end' ? 'flex-end' : 'flex-start'}>
    <Text textStyle="v2.tableHeader" color="v2.text.subtle">
      {children}
    </Text>
  </Stack>
);

const DecreaseOrderCard = ({
  order,
  position,
}: {
  order: DecreaseOrderRow;
  position?: Position;
}) => {
  const triggerPrice = toReadableValue(order.triggerPrice, USD_DECIMALS);
  const { cancel, isPending } = useCancelDecreaseOrder();
  const canCancel = order.orderHash !== ZERO_ORDER_HASH;
  const canEdit = canCancel && position;

  return (
    <Grid
      gridTemplateColumns={TABLE_COLUMNS}
      columnGap={3}
      rowGap={0}
      px={4}
      py={2}
      display={{ base: 'none', desktop: 'grid' }}
      alignItems="center"
      borderBottom="1px solid"
      borderBottomColor="v2.border.hairline"
      w="full"
      transition="background 120ms"
      _hover={{ bg: 'rgba(255, 255, 255, 0.035)' }}
    >
      <PairSideCell
        image={position?.pair.image}
        name={position?.pair.baseName ?? 'Unknown'}
        pill={position ? <PositionSidePill position={position} /> : undefined}
      />
      <OrderKindPill kind={order.kind} />
      <DecreaseOrderTrigger triggerPrice={triggerPrice} position={position} />
      <DecreaseOrderCloseQty order={order} position={position} />
      <DecreaseOrderEstimatedPnl
        closeQty={toReadableValue(order.closeQty, PAIR_AMOUNT_DECIMALS)}
        triggerPrice={triggerPrice}
        position={position}
      />
      <HStack gap="1" justifyContent="flex-start">
        {canEdit && (
          <EditDecreaseOrderOverlay order={order} position={position}>
            <Button
              variant="ghost"
              size="small"
              p="0"
              minW="28px"
              h="28px"
              aria-label="Update order"
            >
              <Icon boxSize="14px" color="v2.text.muted">
                <Edit />
              </Icon>
            </Button>
          </EditDecreaseOrderOverlay>
        )}
        <RequireConnectionClickable>
          <Button
            variant="ghost"
            size="small"
            p="0"
            minW="28px"
            h="28px"
            loading={isPending}
            disabled={!canCancel}
            onClick={() => canCancel && cancel(order.orderHash)}
            aria-label="Cancel order"
          >
            <Icon boxSize="14px" color="v2.text.muted">
              <XCircle />
            </Icon>
          </Button>
        </RequireConnectionClickable>
      </HStack>
    </Grid>
  );
};

const DecreaseOrderTrigger = ({
  triggerPrice,
  position,
}: {
  triggerPrice: BigNumber;
  position?: Position;
}) => {
  const priceDisplayDecimals = useTokenDisplayDecimals(
    position?.pair.baseSymbol,
    position?.pair.priceDisplayDecimals,
  );

  if (!position) {
    return (
      <AmountUsd
        value={triggerPrice}
        css={numericValueStyle}
        opts={{ displayDecimals: priceDisplayDecimals }}
      />
    );
  }

  return (
    <DecreaseOrderTriggerWithPosition
      triggerPrice={triggerPrice}
      position={position}
      priceDisplayDecimals={priceDisplayDecimals}
    />
  );
};

const DecreaseOrderTriggerWithPosition = ({
  triggerPrice,
  position,
  priceDisplayDecimals,
}: {
  triggerPrice: BigNumber;
  position: Position;
  priceDisplayDecimals: number | undefined;
}) => {
  const livePairMarketPrice = usePriceLive(position.pair.baseSymbol);
  const markPrice = livePairMarketPrice ?? position.entryPrice;
  const distancePct = markPrice.isZero()
    ? undefined
    : triggerPrice.minus(markPrice).div(markPrice).abs().times(100);

  return (
    <Stack gap="0" alignItems="flex-start" minW={0}>
      <AmountUsd
        value={triggerPrice}
        css={numericValueStyle}
        opts={{ displayDecimals: priceDisplayDecimals }}
      />
      <Amount
        value={distancePct}
        textStyle="numbers.xsmall"
        color="v2.text.muted"
        opts={{
          displayDecimals: 2,
          suffix: '% from mark',
        }}
      />
    </Stack>
  );
};

const OrderKindPill = ({ kind }: { kind: DecreaseOrderRow['kind'] }) => (
  <HStack
    w="42px"
    justifyContent="center"
    px="6px"
    py="2px"
    borderRadius="2px"
    border="1px solid"
    borderColor={kind === 'TP' ? 'v2.long.border' : 'v2.short.border'}
    bg={kind === 'TP' ? 'v2.long.dim' : 'v2.short.dim'}
    color={kind === 'TP' ? 'v2.long.default' : 'v2.short.default'}
    fontFamily="Inter"
    fontSize="10px"
    fontWeight="500"
    letterSpacing="0.04em"
  >
    {kind}
  </HStack>
);

const DecreaseOrderCloseQty = ({
  order,
  position,
}: {
  order: DecreaseOrderRow;
  position?: Position;
}) => {
  if (!position) {
    return <DecreaseOrderCloseQtyFallback order={order} />;
  }

  return (
    <DecreaseOrderCloseQtyWithPosition order={order} position={position} />
  );
};

const DecreaseOrderCloseQtyFallback = ({
  order,
}: {
  order: DecreaseOrderRow;
}) => {
  const closeQty = toReadableValue(order.closeQty, PAIR_AMOUNT_DECIMALS);

  return (
    <Stack gap="0" alignItems="flex-start" minW={0}>
      <Amount value={closeQty} css={numericValueStyle} />
    </Stack>
  );
};

const DecreaseOrderCloseQtyWithPosition = ({
  order,
  position,
}: {
  order: DecreaseOrderRow;
  position: Position;
}) => {
  const closeQty = toReadableValue(order.closeQty, PAIR_AMOUNT_DECIMALS);
  const closePct = !position.size.isZero()
    ? closeQty.div(position.size).times(100)
    : undefined;
  const sizeDisplayDecimals = useTokenSizeDisplayDecimals(
    position.pair.baseSymbol,
  );
  const livePairMarketPrice = usePriceLive(position.pair.baseSymbol);
  const closeQtyUsd = livePairMarketPrice
    ? closeQty.multipliedBy(livePairMarketPrice)
    : closeQty.multipliedBy(position.entryPrice);

  return (
    <Stack gap="0" alignItems="flex-start" minW={0}>
      <HStack gap="1" justifyContent="flex-start" minW={0}>
        <Amount
          value={closeQty}
          css={numericValueStyle}
          opts={{
            displayDecimals: sizeDisplayDecimals,
            suffix: ` ${position.pair.baseSymbol}`,
          }}
        />
        {closePct && (
          <Text textStyle="numbers.xsmall" color="v2.text.muted">
            ({closePct.toFixed(2)}%)
          </Text>
        )}
      </HStack>
      <AmountUsd
        value={closeQtyUsd}
        textStyle="numbers.xsmall"
        color="v2.text.muted"
      />
    </Stack>
  );
};

const DecreaseOrderEstimatedPnl = ({
  closeQty,
  triggerPrice,
  position,
}: {
  closeQty: BigNumber;
  triggerPrice: BigNumber;
  position?: Position;
}) => {
  if (!position) {
    return (
      <Text css={numericValueStyle} color="v2.text.muted">
        --
      </Text>
    );
  }

  const pnl = position.isLong
    ? triggerPrice.minus(position.entryPrice).multipliedBy(closeQty)
    : position.entryPrice.minus(triggerPrice).multipliedBy(closeQty);

  return (
    <AmountUsd
      value={pnl}
      css={numericValueStyle}
      color={pnl.gte(0) ? 'v2.long.default' : 'v2.short.default'}
      opts={{
        isShowSign: true,
      }}
    />
  );
};

const ZERO_ORDER_HASH =
  '0x0000000000000000000000000000000000000000000000000000000000000000';

const valueStyle = {
  textStyle: 'numbers.small',
  color: 'v2.text.secondary',
  textAlign: 'start',
  fontVariantNumeric: 'tabular-nums',
};

const numericValueStyle = {
  ...valueStyle,
  textAlign: 'start',
};

const useCancelDecreaseOrder = () => {
  const chainId = useDataChainId();
  const invalidatePositionQuery = useInvalidatePositionQuery();
  const { writeContract, isPending } = useTxnWriteContract({
    onSuccess: invalidatePositionQuery,
  });

  return {
    isPending,
    cancel: (orderHash: `0x${string}`) => {
      const address = getContractAddress('LeverUp', chainId);
      if (!address) throw new Error('LeverUp contract address missing');

      return writeContract({
        abi: TradingPortalFacet,
        address,
        functionName: 'cancelDecreaseOrder',
        args: [orderHash],
      });
    },
  };
};
